import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useIntegrationAccess } from '@/contexts/IntegrationAccessContext';
import { NavigationItem, SidebarProps } from '@/types/components';
import {
  APP_CONFIG,
  hasDeveloperAccess,
  hasWarehouseAccess,
  isDeveloperPageEnabled,
} from '@/config/app';

const DASH_CS_WHATSAPP_NUMBER = process.env.REACT_APP_DASH_CS_WHATSAPP_NUMBER;

const BASE_NAVIGATION_ITEMS: NavigationItem[] = [
  {
    id: 'shipments',
    title: 'Shipments',
    router: '/shipments',
    icon: 'ri-box-3-line',
  },
  {
    id: 'deliveries',
    title: 'Deliveries',
    router: '/deliveries',
    icon: 'ri-truck-line',
  },
  {
    id: 'packages',
    title: 'Packages',
    router: '/packages',
    icon: 'ri-archive-stack-line',
  },
  {
    id: 'invoices',
    title: 'Invoices',
    router: '/invoices',
    icon: 'ri-file-list-3-line',
  },
  // Developer (API keys / webhooks) follows the isDeveloperPageEnabled() kill
  // switch, and per client hasDeveloperAccess() on the backend's answer
  // (IntegrationAccessContext, see the filter in Sidebar); the /developer
  // route is gated the same way in routes.tsx (keep the two in sync).
  ...(!isDeveloperPageEnabled()
    ? []
    : [
        {
          id: 'developer',
          title: 'Developer',
          router: '/developer',
          icon: 'ri-code-s-slash-line',
        },
      ]),
  {
    id: 'support',
    title: 'Support',
    // WhatsApp chat with Dash support, number from REACT_APP_DASH_CS_WHATSAPP_NUMBER, opened in a new tab.
    router: `https://wa.me/${DASH_CS_WHATSAPP_NUMBER}`,
    icon: 'ri-customer-service-2-line',
    external: true,
  },
  // {
  //   id: 'showcase',
  //   title: 'DS Showcase',
  //   router: '/showcase',
  //   icon: 'ri-palette-line',
  // },
];

// Read-only WMS pages, grouped under a collapsible "Warehouse" entry right
// after Invoices. Shown only to clients the backend enables the WMS API for
// (IntegrationAccessContext); the /warehouse/* routes are gated the same way
// (keep the two in sync).
const WAREHOUSE_ROOT = '/warehouse';
const WAREHOUSE_ITEMS: NavigationItem[] = [
  {
    id: 'warehouse-stock',
    title: 'Stock',
    router: '/warehouse/stock',
    icon: 'ri-stack-line',
  },
  {
    id: 'warehouse-sku',
    title: 'SKU',
    router: '/warehouse/sku',
    icon: 'ri-barcode-line',
  },
  {
    id: 'warehouse-outbound',
    title: 'Outbound request',
    router: '/warehouse/outbound',
    icon: 'ri-upload-2-line',
  },
  {
    id: 'warehouse-purchase-order',
    title: 'Purchase order',
    router: '/warehouse/purchase-order',
    icon: 'ri-file-list-2-line',
  },
  {
    id: 'warehouse-inbound',
    title: 'Inbound request',
    router: '/warehouse/inbound',
    icon: 'ri-download-2-line',
  },
];

/** A list page stays active on its detail pages (`/warehouse/outbound/:id`). */
const isUnder = (path: string, router: string) =>
  path === router || path.startsWith(`${router}/`);

const renderMenuItem = (
  item: NavigationItem,
  isActive: boolean,
  isCollapsed: boolean,
  extraClasses: string = '',
  onNavigate?: () => void
) => {
  const baseClasses = isCollapsed
    ? `w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${extraClasses}`
    : `group flex items-center px-3 py-2 text-sm font-sans font-medium rounded-lg transition-colors ${extraClasses}`;

  const activeClasses = isActive
    ? isCollapsed
      ? 'bg-feature-lighter text-primary'
      : 'bg-feature-lighter text-primary border-r-2 border-primary'
    : isCollapsed
      ? 'text-text-soft-400 hover:bg-bg-weak-50 hover:text-text-sub-600'
      : 'text-text-sub-600 hover:bg-bg-weak-50 hover:text-text-strong-950';

  const disabledClasses = item.disabled
    ? 'opacity-50 cursor-not-allowed pointer-events-none'
    : '';

  const className = `${baseClasses} ${activeClasses} ${disabledClasses}`.trim();

  const inner = (
    <>
      <i
        className={`${item.icon} text-[20px] ${
          isCollapsed
            ? ''
            : isActive
              ? 'text-primary'
              : 'text-text-soft-400 group-hover:text-text-sub-600'
        }`}
      ></i>
      {!isCollapsed && (
        <>
          <span className="ml-3">{item.title}</span>
          {item.badge && (
            <span className="ml-auto mr-2 px-2 py-0.5 text-xs font-medium bg-feature-lighter text-primary rounded-full">
              {item.badge}
            </span>
          )}
          <i
            className={`${
              item.external ? 'ri-external-link-line' : 'ri-arrow-right-s-line'
            } text-[16px] ml-auto`}
          ></i>
        </>
      )}
      {isCollapsed && item.badge && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-static-white text-xs rounded-full flex items-center justify-center">
          {typeof item.badge === 'number' && item.badge > 9 ? '9+' : item.badge}
        </span>
      )}
    </>
  );

  // External items (e.g. WhatsApp support) open in a new tab via a plain anchor.
  if (item.external) {
    return (
      <a
        key={item.id}
        href={item.router}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        title={isCollapsed ? item.title : undefined}
        onClick={() => onNavigate?.()}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link
      key={item.id}
      to={item.disabled ? '#' : item.router}
      className={className}
      title={isCollapsed ? item.title : undefined}
      onClick={(e) => {
        if (item.disabled) {
          e.preventDefault();
          return;
        }
        onNavigate?.();
      }}
    >
      {inner}
    </Link>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
  onNavigate,
  currentPath = APP_CONFIG.homeRoute,
}) => {
  const { user, signOut } = useAuth();
  // Developer only for clients allowed an API (shipment or WMS order); both
  // stay hidden until the backend has answered.
  const { access } = useIntegrationAccess();
  const navigationItems: NavigationItem[] = BASE_NAVIGATION_ITEMS.filter(
    (item) => item.id !== 'developer' || hasDeveloperAccess(access)
  );
  const showWarehouse = hasWarehouseAccess(access);
  const inWarehouse = isUnder(currentPath, WAREHOUSE_ROOT);
  const [warehouseOpen, setWarehouseOpen] = useState(inWarehouse);
  // Landing on a warehouse page (link, back button) opens the group.
  useEffect(() => {
    if (inWarehouse) setWarehouseOpen(true);
  }, [inWarehouse]);

  // Collapsed rail: one icon that opens Stock. Expanded: a header that
  // toggles the sub-menu.
  const renderWarehouse = (isCollapsed: boolean) => {
    if (!showWarehouse) return null;
    if (isCollapsed) {
      return (
        <div key="warehouse" className="relative">
          {renderMenuItem(
            {
              id: 'warehouse',
              title: 'Warehouse',
              router: WAREHOUSE_ITEMS[0].router,
              icon: 'ri-building-4-line',
            },
            inWarehouse,
            true,
            '',
            onNavigate
          )}
        </div>
      );
    }
    return (
      <div key="warehouse">
        <button
          type="button"
          onClick={() => setWarehouseOpen((v) => !v)}
          aria-expanded={warehouseOpen}
          className={`group flex w-full items-center px-3 py-2 text-sm font-sans font-medium rounded-lg transition-colors ${
            inWarehouse
              ? 'text-primary'
              : 'text-text-sub-600 hover:bg-bg-weak-50 hover:text-text-strong-950'
          }`}
        >
          <i
            className={`ri-building-4-line text-[20px] ${
              inWarehouse
                ? 'text-primary'
                : 'text-text-soft-400 group-hover:text-text-sub-600'
            }`}
          ></i>
          <span className="ml-3">Warehouse</span>
          <i
            className={`ri-arrow-down-s-line text-[16px] ml-auto transition-transform ${
              warehouseOpen ? 'rotate-180' : ''
            }`}
          ></i>
        </button>
        {warehouseOpen && (
          <div className="mt-1 ml-5 space-y-1 border-l border-stroke-soft-200 pl-2">
            {WAREHOUSE_ITEMS.map((item) =>
              renderMenuItem(
                item,
                isUnder(currentPath, item.router),
                false,
                '',
                onNavigate
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {}
  };

  return (
    <div
      className={`bg-bg-white-0 border-r border-stroke-soft-200 flex flex-col h-full transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div
        className={`flex items-center ${
          isCollapsed ? 'justify-center p-4' : 'justify-between p-4'
        } border-b border-stroke-soft-200`}
      >
        <div
          className={`flex ${
            isCollapsed
              ? 'flex-col items-center space-y-2'
              : 'flex-row items-center space-x-3'
          }`}
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center">
            <img
              src="/images/Dash-Official-Logo.png"
              alt={`${APP_CONFIG.shortName} logo`}
              className="w-6 h-6"
            />
          </div>
          {isCollapsed && (
            <button
              onClick={onToggleCollapse}
              className="hidden md:flex p-2 rounded-lg hover:bg-bg-weak-50 transition-colors"
            >
              <i className="ri-sidebar-unfold-line text-[20px] text-icon-sub-600"></i>
            </button>
          )}
          {!isCollapsed && (
            <div>
              <h1 className="text-lg font-sans font-semibold text-text-strong-950">
                Dash Link
              </h1>
            </div>
          )}
        </div>
        {!isCollapsed && (
          <button
            onClick={onToggleCollapse}
            className="hidden md:flex p-1 rounded-lg hover:bg-bg-weak-50 transition-colors"
          >
            <i className="ri-sidebar-fold-line text-[20px] text-icon-sub-600"></i>
          </button>
        )}
      </div>

      <nav
        className={`flex-1 flex flex-col ${
          isCollapsed ? 'items-center space-y-6 py-4' : 'px-3 py-4'
        }`}
      >
        {isCollapsed ? (
          <>
            {navigationItems.map((item) => {
              const isActive = currentPath === item.router;
              return (
                <React.Fragment key={item.id}>
                  <div className="relative">
                    {renderMenuItem(item, isActive, true, '', onNavigate)}
                  </div>
                  {item.id === 'invoices' && renderWarehouse(true)}
                </React.Fragment>
              );
            })}
          </>
        ) : (
          <div className="space-y-1">
            <div className="text-xs font-sans font-medium text-text-soft-400 uppercase tracking-wide mb-3">
              Main
            </div>

            {navigationItems.map((item) => {
              const isActive = currentPath === item.router;
              return (
                <React.Fragment key={item.id}>
                  {renderMenuItem(item, isActive, false, '', onNavigate)}
                  {item.id === 'invoices' && renderWarehouse(false)}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </nav>

      {isCollapsed ? (
        <div className="p-4 flex flex-col items-center space-y-4">
          {user && (
            <div className="relative group mt-4">
              <img
                src={
                  user.photoURL ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    user.displayName || user.email || 'User'
                  )}&background=5e2aac&color=fff`
                }
                alt={user.displayName || user.email || 'User'}
                className="w-8 h-8 rounded-full cursor-pointer"
                title={user.displayName || user.email || 'User'}
              />

              <button
                onClick={onToggleCollapse}
                className="absolute -top-1 -right-1 w-4 h-4 bg-bg-white-0 border border-stroke-soft-200 rounded-full flex items-center justify-center hover:bg-bg-weak-50 transition-colors"
                title="Expand sidebar"
              >
                <i className="ri-arrow-right-s-line text-[8px]"></i>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="border-t border-stroke-soft-200 p-3">
          {user && (
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-bg-weak-50 mt-1">
              <img
                src={
                  user.photoURL ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    user.displayName || user.email || 'User'
                  )}&background=5e2aac&color=fff`
                }
                alt={user.displayName || user.email || 'User'}
                className="w-8 h-8 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-sans font-medium text-text-strong-950 truncate">
                  {user.displayName || user.firstName || 'User'}
                </p>
                <p className="text-xs font-sans text-text-soft-400 truncate">
                  {user.email}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-1 rounded-lg hover:bg-error-lighter hover:text-error-base transition-colors group"
                title="Logout"
              >
                <i className="ri-logout-box-line text-[16px] text-text-soft-400 group-hover:text-error-base"></i>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
